import React, { useEffect, useState } from 'react';
import { FiX as X, FiShare as Share, FiPlusSquare as PlusSquare, FiDownload as Download } from 'react-icons/fi';

const DISMISS_KEY = 'pgi.install.dismissedAt';
const DISMISS_DAYS = 14;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

const recentlyDismissed = () => {
  try {
    const at = window.localStorage.getItem(DISMISS_KEY);
    if (!at) return false;
    const days = (Date.now() - Number(at)) / 86400000;
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
};

/**
 * Invites the client to install the app to their home screen.
 *
 * Chrome/Edge/Android fire `beforeinstallprompt`, which gives us a real
 * install button. iOS Safari has no such event, so we show the Share-sheet
 * instructions instead.
 */
const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS gets the manual hint, but only after a moment so it doesn't
    // interrupt the first paint.
    let timer;
    if (isIos()) timer = setTimeout(() => setShowIosHint(true), 2500);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Dismissal just won't persist; harmless.
    }
    setDeferredPrompt(null);
    setShowIosHint(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  if (!deferredPrompt && !showIosHint) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] lg:bottom-6 lg:left-auto lg:right-6 lg:max-w-sm lg:p-0">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#DC143C] to-[#B01030] flex items-center justify-center shrink-0">
            <Download size={22} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900">Keep your insurance handy</h3>
            {deferredPrompt ? (
              <p className="text-sm text-gray-600 mt-1">
                Add Pinoy General to your home screen for instant access to your ID cards — even
                without signal.
              </p>
            ) : (
              <p className="text-sm text-gray-600 mt-1 flex flex-wrap items-center gap-1">
                Tap <Share size={15} className="inline text-[#0038A8]" aria-label="the Share button" />
                then <PlusSquare size={15} className="inline text-[#0038A8]" aria-label="Add to Home Screen" />
                <span className="font-medium">Add to Home Screen</span>
              </p>
            )}
            {deferredPrompt && (
              <button
                onClick={install}
                className="mt-3 w-full bg-[#DC143C] text-white py-2.5 px-4 rounded-lg font-semibold hover:bg-[#B01030] transition-colors"
              >
                Add to Home Screen
              </button>
            )}
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="p-1 text-gray-400 hover:text-gray-600 shrink-0"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
